"use client";

import { useState } from "react";
import { Check, LoaderCircle, Share2 } from "lucide-react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ShareScoreProps {
  businessName: string;
  city: string;
  score: number;
  grade: string;
  /** URL to share (defaults to current page) */
  shareUrl?: string;
  /** Compact mode — icon-only button */
  compact?: boolean;
  /** Button variant passed to the underlying Button */
  variant?: "primary" | "secondary" | "ghost" | "outline";
  /** Use pill shape (rounded-full) */
  rounded?: boolean;
  /** Optional check id to attach to `report_shared` events. */
  checkId?: string | null;
  /** Optional place id (UUID) to attach to `report_shared` events. */
  placeId?: string | null;
}

function buildOgUrl(params: { name: string; city: string; score: number; grade: string }) {
  const base = typeof window !== "undefined" ? window.location.origin : "https://app.rekkd.io";
  const url = new URL("/api/og/score", base);
  url.searchParams.set("name", params.name);
  url.searchParams.set("city", params.city);
  url.searchParams.set("score", String(params.score));
  url.searchParams.set("grade", params.grade);
  return url.toString();
}

export function ShareScore({
  businessName,
  city,
  score,
  grade,
  shareUrl,
  compact = false,
  variant = "secondary",
  rounded = false,
  checkId = null,
  placeId = null,
}: ShareScoreProps) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const url = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const text = `${businessName} scored ${score}/100 on AI visibility (${grade}). Check your restaurant's AI score free →`;

  const trackShare = (method: "twitter" | "linkedin" | "copy" | "download") => {
    try {
      posthog.capture("report_shared", {
        method,
        check_id: checkId,
        place_id: placeId,
        score,
        grade,
        share_url: url,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  const shareOptions = [
    {
      label: "Twitter / X",
      onClick: () => {
        trackShare("twitter");
        window.open(
          `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
          "_blank",
          "width=600,height=400"
        );
        setMenuOpen(false);
      },
    },
    {
      label: "LinkedIn",
      onClick: () => {
        trackShare("linkedin");
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          "_blank",
          "width=600,height=400"
        );
        setMenuOpen(false);
      },
    },
    {
      label: "Copy link",
      onClick: async () => {
        setLoadingAction("Copy link");
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          trackShare("copy");
          setTimeout(() => setCopied(false), 2000);
        } catch {
          /* fallback — select text */
        }
        setLoadingAction(null);
        setMenuOpen(false);
      },
    },
    {
      label: "Download image",
      onClick: async () => {
        setLoadingAction("Download image");
        trackShare("download");
        try {
          const ogUrl = buildOgUrl({ name: businessName, city, score, grade });
          const res = await fetch(ogUrl);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `${businessName.replace(/\s+/g, "-").toLowerCase()}-ai-score.png`;
          link.click();
          URL.revokeObjectURL(blobUrl);
        } catch {
          /* fallback — direct link */
          const ogUrl = buildOgUrl({ name: businessName, city, score, grade });
          const link = document.createElement("a");
          link.href = ogUrl;
          link.download = `${businessName.replace(/\s+/g, "-").toLowerCase()}-ai-score.png`;
          link.click();
        }
        setLoadingAction(null);
        setMenuOpen(false);
      },
    },
  ];

  return (
    <div className="relative">
      <Button
        variant={variant}
        size={compact ? "icon-sm" : "sm"}
        className={rounded ? "rounded-[var(--radius-full)]" : undefined}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Share score"
      >
        {copied ? (
          <Check className="size-4 text-[var(--color-success)]" />
        ) : (
          <Share2 className="size-4" />
        )}
        {!compact && <span>Share</span>}
      </Button>

      {menuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[var(--z-overlay)]"
            onClick={() => setMenuOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 top-full z-[var(--z-popover)] mt-[var(--space-2)] min-w-[10rem] rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-[var(--space-1)] shadow-[var(--shadow-lg)]">
            {shareOptions.map((option) => (
              <button
                key={option.label}
                type="button"
                disabled={loadingAction === option.label}
                onClick={option.onClick}
                className="flex w-full items-center gap-[var(--space-2)] rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-2)] text-left text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-muted)] disabled:opacity-50"
              >
                {loadingAction === option.label ? (
                  <LoaderCircle className="size-4 shrink-0 animate-spin text-[var(--color-text-muted)]" />
                ) : null}
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
